'use client';

/**
 * AskProseCard — ONE by-value prose card for every Ask Klyde text slot.
 *
 * Fast-pair text, LLM synthesis, slot-5 narration: the slots differ only
 * in DATA (label, body, tint), so they share this one primitive instead
 * of a bespoke card each. The exception that stays separate is
 * content_answer — by-REFERENCE structured blocks from an authored
 * entity; a different contract, not a different coat of paint.
 *
 * Two layers, composed:
 *   - `ProseBlock`   — the pure visual (no registration). The rail's
 *     conversation thread renders this directly, because the rail TURN
 *     already registers the whole slot frame in one scene entry —
 *     per-block registration there would double-count.
 *   - `AskProseCard` — ProseBlock + `useCardExport`. What the registry
 *     mounts on the overlay (and any future preset), where each
 *     placement IS the scene unit.
 *
 * @see klyde_edge/ui_config/composition/cards/ask.py — @card declaration
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain copilot
 */

import { Sparkle } from 'lucide-react';
import { useCardExport } from '@/lib/design/card-export';
import { text } from '@/lib/design/tokens';
import { cn } from '@/lib/utils/cn';

export type ProseTone = 'default' | 'accent';

/** Pure visual — a labelled prose block. No registration side-effects.
 *  Type via tokens (em-relative, scales off the surface base); the box
 *  (border, tint, padding) is container chrome and stays inline. */
export function ProseBlock({
  label,
  body,
  secondary,
  tone = 'default',
  mutedBody = false,
  provisional = false,
}: {
  label?: string;
  body?: string;
  /** Deeper reading under the body (slot 2 under slot 1) — muted. */
  secondary?: string;
  tone?: ProseTone;
  /** Render the body at the slot-2 reading style (text.muted) — used by
   *  the rail's synthesis (slot 3) and narration (slot 5). */
  mutedBody?: boolean;
  /** The content is a fast-tier stand-in that may still change: the FRAME
   *  breathes (pulsing ring — never the text) until the answer settles. */
  provisional?: boolean;
}) {
  if (!body) return null;
  return (
    <div
      className={cn(
        'relative rounded-lg border px-4 py-3',
        tone === 'accent' ? 'border-primary/20 bg-primary/5' : 'border-border bg-card',
      )}
    >
      {provisional && (
        <div className="pointer-events-none absolute inset-0 rounded-lg ring-2 ring-primary/60 bg-primary/10 animate-pulse" />
      )}
      {label && (
        <div className={cn(text.eyebrow, 'mb-1 flex items-center gap-1.5')}>
          <Sparkle className={cn('h-3 w-3 text-primary/60', provisional && 'animate-pulse')} />
          {label}
        </div>
      )}
      <div className={cn(mutedBody ? text.muted : text.body, 'leading-relaxed whitespace-pre-wrap')}>{body}</div>
      {secondary && secondary !== body && (
        <div className={cn(text.muted, 'mt-2 leading-relaxed whitespace-pre-wrap')}>{secondary}</div>
      )}
    </div>
  );
}

/** Registry card — ProseBlock that declares itself to scene capture. */
export function AskProseCard({
  label,
  body,
  secondary,
  tone,
}: {
  label?: string;
  body?: string;
  secondary?: string;
  tone?: ProseTone;
}) {
  useCardExport({
    label,
    content_key: 'ask_prose',
    text: body ?? '',
    json: { label: label ?? null, body: body ?? null, secondary: secondary ?? null },
    status: body ? 'loaded' : 'idle',
    // By-value card — no fetch of its own; payload truth rides the
    // ask-compose cache entry the rail turn already declares.
    query_key: null,
  });
  return <ProseBlock label={label} body={body} secondary={secondary} tone={tone} />;
}
